import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, X, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';
import { CalendarCard } from './CalendarCard';

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => iso(new Date());

const fmtEventDay = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const fmtStart = (start, allDay) => {
  if (allDay) return 'All day';
  const d = new Date(start);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Minimal new-event modal.
const NewEventModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today());
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('13:00');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle(''); setDescription('');
      setStartTime('12:00'); setEndTime('13:00'); setDate(today());
    }
  }, [open]);

  if (!open) return null;

  const save = async () => {
    if (!title.trim()) {
      toast.error('Add a title');
      return;
    }
    setBusy(true);
    try {
      const s = `${date}T${startTime}:00`;
      const e = `${date}T${endTime}:00`;
      await nexaApi.post('/google/events', {
        title: title.trim(),
        start: s,
        end: e,
        description: description.trim() || null,
      });
      toast.success('Event added');
      onCreated();
      onClose();
    } catch {
      toast.error('Could not create event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="nexa-new-event-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0A0A0A] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">New event</span>
          <button onClick={onClose} data-testid="nexa-new-event-close" aria-label="Close"
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white/90">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Event title" autoFocus
          data-testid="nexa-new-event-title"
          className="w-full bg-transparent border-b border-white/15 text-lg text-white/90 py-2 focus:outline-none focus:border-sky-400/60"
        />
        <div className="grid grid-cols-3 gap-3 mt-5">
          <label className="col-span-3 sm:col-span-1 text-[10px] font-mono tracking-widest uppercase text-white/40">
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="nexa-new-event-date"
              className="w-full mt-1 bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white/85 focus:outline-none focus:border-sky-400/60"
            />
          </label>
          <label className="text-[10px] font-mono tracking-widest uppercase text-white/40">
            Start
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              data-testid="nexa-new-event-start"
              className="w-full mt-1 bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white/85 focus:outline-none focus:border-sky-400/60"
            />
          </label>
          <label className="text-[10px] font-mono tracking-widest uppercase text-white/40">
            End
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              data-testid="nexa-new-event-end"
              className="w-full mt-1 bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white/85 focus:outline-none focus:border-sky-400/60"
            />
          </label>
        </div>
        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Notes (optional)" rows={3}
          data-testid="nexa-new-event-notes"
          className="w-full mt-4 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 focus:outline-none focus:border-sky-400/60"
        />
        <button
          onClick={save} disabled={busy}
          data-testid="nexa-new-event-save"
          className="mt-5 w-full h-11 rounded-xl bg-white text-black font-semibold disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add to Google Calendar'}
        </button>
      </motion.div>
    </div>
  );
};

export const CalendarDetail = () => {
  const [upcoming, setUpcoming] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = () => {
    nexaApi
      .get('/google/upcoming', { params: { days: 30 } })
      .then(({ data }) => setUpcoming(data))
      .catch(() => setUpcoming({ connected: false, events: [] }));
  };

  useEffect(() => {
    load();
  }, [reloadKey]);

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await nexaApi.delete(`/google/events/${encodeURIComponent(ev.id)}`);
      toast.success('Event deleted');
      setReloadKey((k) => k + 1);
    } catch {
      toast.error('Could not delete');
    }
  };

  if (upcoming && upcoming.connected === false) {
    return (
      <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-calendar-not-connected">
        <p className="text-sm text-white/60">
          Connect Google Calendar in <span className="text-white/85">Settings</span> to see and edit your schedule here.
        </p>
      </div>
    );
  }

  // Group upcoming events by date.
  const byDay = {};
  (upcoming?.events || []).forEach((ev) => {
    const day = (ev.start || '').slice(0, 10);
    if (!day) return;
    (byDay[day] = byDay[day] || []).push(ev);
  });
  const days = Object.keys(byDay).sort();

  return (
    <div className="flex flex-col gap-6" data-testid="nexa-calendar-detail">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          Today &amp; browse
        </span>
        <button
          onClick={() => setModalOpen(true)}
          data-testid="nexa-calendar-new"
          className="h-9 px-4 rounded-full bg-sky-400 text-black text-xs font-semibold tracking-wider uppercase flex items-center gap-1.5 active:scale-95 transition-all"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> New event
        </button>
      </div>

      <CalendarCard key={reloadKey} />

      <div>
        <span className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          Next 30 days ({upcoming?.events?.length || 0})
        </span>
        <div className="mt-3 rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
          {upcoming === null ? (
            <div className="px-5 py-6 flex justify-center">
              <div className="w-4 h-4 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
            </div>
          ) : days.length === 0 ? (
            <p className="text-sm text-white/40 px-5 py-5">Nothing scheduled in the next 30 days.</p>
          ) : days.map((day, di) => (
            <div key={day} className={di < days.length - 1 ? 'border-b border-white/[0.06]' : ''}>
              <div className="px-5 pt-3 pb-1">
                <span className="text-[10px] font-mono tracking-widest uppercase text-white/40">
                  {fmtEventDay(day)}
                </span>
              </div>
              {byDay[day].map((ev, ei) => (
                <div key={ev.id}
                  className={`px-5 py-3 flex items-start gap-3 ${
                    ei < byDay[day].length - 1 ? 'border-b border-white/[0.04]' : ''
                  }`}
                  data-testid={`nexa-upcoming-event-${ev.id}`}
                >
                  <div className="pt-1.5"><div className="w-1.5 h-1.5 rounded-full bg-sky-400" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-mono text-sky-400/80 tracking-widest">
                      {fmtStart(ev.start, ev.all_day)}
                    </p>
                    <p className="text-sm text-white/90 mt-0.5">{ev.title}</p>
                    {ev.location && (
                      <p className="text-[11px] text-white/40 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" strokeWidth={1.5} /> {ev.location}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => deleteEvent(ev)}
                    data-testid={`nexa-upcoming-delete-${ev.id}`}
                    aria-label="Delete event"
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white/30 hover:text-red-400 active:scale-90 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/30 mt-2 px-1 flex items-center gap-1.5">
          <CalendarDays className="w-3 h-3" strokeWidth={1.5} />
          Tell NEXA in chat to add, cancel or reschedule any event.
        </p>
      </div>

      <NewEventModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
};
